import { useState } from 'react'
import { Link } from 'react-router-dom'

// One column of the comparison — P5-3 T9, HANDOFF screen 2.
//
// 🔴 THIS FILE IMPORTS NO PERSON-RENDERING HELPER, ON PURPOSE. Every person it
// draws arrives pre-described by `compare.js::describePeople` — the single seam
// P6-63's ruling will land on. A second `avatarToken(...)` call here would be
// the beginning of the smear that seam exists to prevent.
//
// Top to bottom, and every section is the mock's: photo header, cost block,
// category counts, must-dos, footer. One is missing on purpose; see below.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE WEATHER STRIP IS ABSENT, NOT BROKEN, AND NOT "unavailable"
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF's column ends with a weather strip and its source line ("ten-year
// averages for that week, not a forecast"). Weather is **T10**, and the slice
// plan is explicit about what the state before T10 is: *"before this slice
// exists the grid OMITS enrichment fields entirely; labeled-absence is for
// provider failure once the pipeline exists"*.
//
// So there is no strip, no placeholder, and no "weather unavailable" chip. A
// labeled absence is a report about a provider, and there is no provider to
// report on — rendering one would be this screen claiming a pipeline exists and
// is down. src/test/trips/compare.test.jsx asserts the absence in words rather
// than by a missing testid, because a testid nobody renders is not evidence.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE THREE HONESTY RULES, ON A SCREEN THAT MULTIPLIES THEM
// ═══════════════════════════════════════════════════════════════════════════
//
// All three are visible on every column, never behind a tooltip — the same rule
// the board's roll-up bar obeys, and it is sharper here: two figures side by
// side are an invitation to subtract, and the coverage line is what says the
// two are measuring different amounts of information. A column priced on 4 of 7
// entries beside one priced on 13 of 20 is not "cheaper", and only the labels
// say so.
//
// 🔴 AND THERE IS NO TOTAL. Not across currencies, not across columns. The
// per-currency figures come from `RollUpRead.subtotals`, which carries no
// scalar total "so the shape does not offer one", and nothing here reduces the
// list. A cross-column sum would be worse still: it is a number for a trip that
// goes to both places, which is not a trip anybody is planning.

/** The column header. **P5-3 T8 put the photo in it.**
 *
 * The stripe stays underneath rather than being replaced: it is what shows
 * while the image loads, what shows when a column has no unfurled photo at
 * all, and what the glow and the roman numeral were designed to sit on. The
 * photo is chosen by `placePhoto` — see there for why it is the chosen entry,
 * then a stay, then anything.
 */
function PhotoHeader({ column }) {
  return (
    <div className="d2d-col__photo" aria-hidden="true">
      {column.photo ? (
        <img
          className="d2d-col__img"
          src={column.photo}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          data-testid={`col-photo-${column.id}`}
        />
      ) : null}
      <span className="d2d-col__glow" style={{ '--d2d-col-accent': `var(${column.token})` }} />
      <span className="d2d-col__roman d2d-micro">{column.roman}</span>
    </div>
  )
}

function Sums({ column }) {
  return (
    <div className="d2d-col__sums" data-testid={`sums-${column.id}`}>
      {column.figures.map((figure) => (
        <div key={figure.currency} className="d2d-col__sumgroup">
          <p className="d2d-col__sumrow">
            <span>
              Σ per person, {figure.perPersonEntryCount}{' '}
              {figure.perPersonEntryCount === 1 ? 'entry' : 'entries'}
            </span>
            <span className="d2d-col__sumvalue">{figure.perPersonComponent}</span>
          </p>
          {/* 🔴 The flat side is shown as the service split it, never divided
              here. `flat_component` is already ÷ headcount; the selection
              beneath it names the entries that total was taken from, with the
              un-divided amount, so the arithmetic is legible without this
              renderer performing any of it. */}
          <p className="d2d-col__sumrow">
            <span>
              flat, split {column.crew} {column.crew === 1 ? 'way' : 'ways'} ·{' '}
              {figure.flatEntryCount} {figure.flatEntryCount === 1 ? 'entry' : 'entries'}
            </span>
            <span className="d2d-col__sumvalue">+ {figure.flatComponent}</span>
          </p>
          <p className="d2d-col__sumtotal">
            <span>≈ each</span>
            <span className="d2d-col__sumvalue">{figure.each}</span>
          </p>
        </div>
      ))}
      {column.notes.map((note) => (
        <p key={note.key} className="d2d-col__sumnote">
          {note.text}
          {note.titles.length > 0 ? <span className="d2d-col__sumtitles">{note.titles.join(' · ')}</span> : null}
        </p>
      ))}
      <p className="d2d-col__sumcaveat">{column.unpricedSentence}</p>
    </div>
  )
}

function Musts({ column, limit }) {
  const shown = limit ? column.musts.slice(0, limit) : column.musts
  const more = column.musts.length - shown.length

  return (
    <div className="d2d-col__musts">
      <p className="d2d-col__eyebrow d2d-micro">Must-dos · {column.mustLine}</p>
      <ul className="d2d-col__mustlist">
        {shown.map((must) => (
          <li key={must.id} className="d2d-col__must" data-testid={`must-${must.id}`}>
            <p className="d2d-col__musthead">
              <span className="d2d-col__muststars d2d-micro">
                <span aria-hidden="true">⭐</span> {must.count}
              </span>
              <span className="d2d-col__musttitle">{must.title}</span>
            </p>
            <p className="d2d-col__mustwho">
              {/* 🔴 A colour per person, a NAME ON HOVER — and NOTHING IN THIS
                  FILE DECIDES EITHER. Every person on this screen arrives
                  already described by `compare.js::describePeople`, which is the
                  one place a user id becomes something a person reads. This maps
                  over what it returned: `token` is the colour, `title` is the
                  whole sentence including the departed marker, `left` is the
                  flag the stylesheet keys on. P6-80 filled that function's name
                  hole and this file's structure did not move — which is what the
                  seam was for.

                  ⚠️ STILL `aria-hidden`, and deliberately. These dots are
                  redundant with the sentence beside them ("all 6 of you", "only
                  Chri"), which is the accessible content and is real text. A
                  `title` on a decorative span is a hover affordance, not an
                  accessible name, and it is not being asked to be one. */}
              <span className="d2d-avatars" aria-hidden="true">
                {must.people.map((person) => (
                  <span
                    key={person.id}
                    className={`d2d-avatar${person.left ? ' d2d-avatar--left' : ''}`}
                    style={{ background: `var(${person.token})` }}
                    title={person.title}
                    data-left={person.left ? 'true' : undefined}
                  />
                ))}
              </span>
              {must.label ? <span className="d2d-col__mustnames">{must.label}</span> : null}
              {must.lone ? (
                <span className="d2d-col__lone d2d-micro" data-testid={`lone-${must.id}`}>
                  {must.loneLabel}
                </span>
              ) : null}
            </p>
          </li>
        ))}
      </ul>
      {more > 0 ? (
        <p className="d2d-col__more d2d-micro" data-testid={`more-${column.id}`}>
          + {more} more starred
        </p>
      ) : null}
      {column.thin ? (
        <p className="d2d-col__thin" data-testid={`thin-${column.id}`}>
          {column.thin}
        </p>
      ) : null}
    </div>
  )
}

export default function CompareColumn({
  column,
  tripId,
  delta,
  limitMusts,
  canChoose,
  onChoose,
  busy = false,
}) {
  const [sumsOpen, setSumsOpen] = useState(false)

  return (
    <article className="d2d-col" data-testid={`place-${column.id}`}>
      <PhotoHeader column={column} />
      <h2 className="d2d-col__name">{column.name}</h2>
      <span
        className="d2d-col__rule"
        aria-hidden="true"
        style={{ '--d2d-col-accent': `var(${column.token})` }}
      />

      <div className="d2d-col__cost">
        <p className="d2d-col__eyebrow d2d-micro">≈ cost per person</p>
        {column.figures.length > 0 ? (
          <ul className="d2d-col__figures">
            {column.figures.map((figure) => (
              <li key={figure.currency} className="d2d-col__figure">
                <span className="d2d-col__amount">{figure.each}</span>
                <span className="d2d-col__currency d2d-micro">{figure.currency}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="d2d-col__amount d2d-col__amount--none">no amounts yet</p>
        )}

        {/* 🔴 P5-3 T10. The converted figure and its as-of date, in ONE
            element — TR7, and it is the only thing that makes a single
            cross-currency number legitimate. Shown only where it says
            something the list above does not: a column already priced in one
            currency would show the same figure twice. */}
        {column.conversion?.state === 'ok' &&
        column.conversion.figure &&
        column.figures.length > 1 ? (
          <p className="d2d-col__converted" data-testid={`converted-${column.id}`}>
            <span className="d2d-col__amount">≈ {column.conversion.figure}</span>{' '}
            <span className="d2d-col__asof d2d-micro">
              all in{column.conversion.asOf ? ` · ${column.conversion.asOf} rates` : ''}
              {column.conversion.stale ? ' · latest we have' : ''}
            </span>
          </p>
        ) : null}

        {delta ? (
          <p className="d2d-col__delta d2d-micro" data-testid={`delta-${column.id}`}>
            {delta}
          </p>
        ) : null}

        <ul className="d2d-col__rules">
          {column.headcount ? (
            <li data-testid={`honesty-headcount-${column.id}`}>{column.headcount}</li>
          ) : null}
          <li data-testid={`honesty-coverage-${column.id}`}>{column.coverage}</li>
          {/* 🔴 THE GUARD MOVED RATHER THAN DISAPPEARING. This line was
              hardcoded as *"N currencies, kept apart — nothing is converted
              yet"*, and its second half became false the moment conversion
              shipped. Its FIRST half is more load-bearing now, not less: two
              figures side by side still look like a total waiting to happen.
              `estimate.js::currencyLine` writes one of three sentences —
              converted, refused-with-a-reason, or nothing at all on a single
              currency — and which is right depends on data this component
              should not be inspecting. */}
          {column.currency ? (
            <li data-testid={`honesty-currency-${column.id}`}>{column.currency}</li>
          ) : null}
          {column.notes.map((note) => (
            <li key={note.key} data-testid={`honesty-basis-${note.rule}-${column.id}`}>
              {note.text}
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="d2d-col__sumstoggle d2d-micro"
          aria-expanded={sumsOpen}
          onClick={() => setSumsOpen((open) => !open)}
        >
          {sumsOpen ? 'hide the sums' : 'show the sums'}
        </button>
        {sumsOpen ? <Sums column={column} /> : null}
      </div>

      <div className="d2d-col__cats">
        <p className="d2d-col__eyebrow d2d-micro">
          On the board · {column.entriesTotal} {column.entriesTotal === 1 ? 'entry' : 'entries'}
        </p>
        {column.bars.map((bar) => (
          <p key={bar.key} className="d2d-col__bar" data-testid={`bar-${column.id}-${bar.key}`}>
            <span className="d2d-col__barlabel">{bar.label}</span>
            <span className="d2d-col__bartrack" aria-hidden="true">
              <span
                className="d2d-col__barfill"
                style={{ width: bar.width, '--d2d-col-accent': `var(${column.token})` }}
              />
            </span>
            <span className="d2d-col__barcount d2d-micro">{bar.count}</span>
          </p>
        ))}
      </div>

      <Musts column={column} limit={limitMusts} />

      <div className="d2d-col__foot">
        {canChoose ? (
          <button
            type="button"
            className="d2d-col__choose"
            disabled={busy}
            data-testid={`choose-${column.id}`}
            onClick={() => onChoose?.(column)}
          >
            Choose {column.name}
          </button>
        ) : null}
        <Link className="d2d-col__open" to={`/trips/${tripId}/places/${column.id}`}>
          Open the {column.name} board
        </Link>
      </div>
    </article>
  )
}
